/**
 * URL utility functions for handling URL parameters and token extraction
 */

/**
 * Extract token from URL query parameters
 * @param url - The URL to extract token from (optional, defaults to current window location)
 * @returns The token string if found, null otherwise
 */
export const extractTokenFromUrl = (url?: string): string | null => {
  try {
    const targetUrl = url || window.location.href;
    const urlObj = new URL(targetUrl);
    return urlObj.searchParams.get('token');
  } catch (error) {
    console.error('Error extracting token from URL:', error);
    return null;
  }
};

/**
 * Extract all query parameters from URL
 * @param url - The URL to extract parameters from (optional, defaults to current window location)
 * @returns Object containing all query parameters
 */
export const extractQueryParams = (url?: string): Record<string, string> => {
  try {
    const targetUrl = url || window.location.href;
    const urlObj = new URL(targetUrl);
    const params: Record<string, string> = {};

    urlObj.searchParams.forEach((value, key) => {
      params[key] = value;
    });

    return params;
  } catch (error) {
    console.error('Error extracting query parameters from URL:', error);
    return {};
  }
};

/**
 * Base url for assets
 * @returns Base url for assets
 */
export const staticUrl = (path: string): string => {
  const base = import.meta.env.BASE_URL || '/';
  if (!path.startsWith('/')) path = '/' + path;
  return `${base}${path}`.replace(/\/{2,}/g, '/');
};

/**
 * get url with parameters
 * @param baseUrl Base url
 * @param path Url path
 * @param params Key-value pair of parameters
 */
export function getUrlWithParams(
  baseUrl: string,
  path: string,
  params?: Record<string, string | number | boolean>
): string {
  let url = baseUrl;
  if (path) {
    url += path.startsWith('/') ? path : `/${path}`;
  }
  if (params) {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      queryParams.append(key, String(value));
    });
    url += `?${queryParams.toString()}`;
  }
  return url;
}
